import React, { useMemo } from "react";
import {
  Box,
  Card,
  Flex,
  Text,
  Button,
  IconButton,
  TextField,
  Switch,
  Progress,
  Heading,
} from "@radix-ui/themes";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { useQuery } from "@tanstack/react-query";
import { IEntity } from "@/types/breeze/Entity";
import { PaginatedResult } from "@/types/breeze/Any";
import ChartRenderer from "./ChartRenderer";
import {
  ChartConfiguration,
  ChartType,
  FunnelMode,
  FunnelStage,
  FunnelStyle,
  PlatformMetricCatalogEntry,
  TimeframePreset,
  XInterval,
  YAgg,
} from "@/types/breeze/ChartConfiguration";
import { chartConfigurationService } from "@/services/breeze/ChartConfigurationService";
import { enumsService } from "@/services/breeze/EnumsService";
import { entityService } from "@/services/breeze/EntityService";
import type {
  BillingInterval,
  PaymentProvider,
  PaymentStatus,
  SubscriptionEventSource,
  SubscriptionEventType,
  SubscriptionStatus,
} from "@/types/breeze/PayEnums";
import SearchableSelect from "@/components/ui/SearchableSelect";
import DeltaBadge from "@/components/ui/DeltaBadge";
import RelationshipFieldPicker from "./RelationshipFieldPicker";
import ChartFilterBuilder from "./ChartFilterBuilder";
import PlatformFilterBuilder from "./PlatformFilterBuilder";
import FunnelChartView from "./FunnelChartView";
import SortableList from "@/components/dnd/SortableList";
import SortableItem from "@/components/dnd/SortableItem";
import { GripVertical, Info, X } from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import {
  AGG_OPTIONS,
  CHART_TYPES,
  INTERVAL_OPTIONS,
  isNumericField,
  isTemporalField,
  isXAxisField,
  TIMEFRAME_OPTIONS,
} from "./chartOptions";

interface ChartCreatorProps {
  draft: ChartConfiguration;
  entityDefinition?: IEntity | null;
  entityData?: PaginatedResult<Record<string, unknown>> | null;
  platformMetric?: PlatformMetricCatalogEntry | null;
  onDraftChange: (next: ChartConfiguration) => void;
}

const TIME_SERIES_TYPES = new Set<ChartType>(["line", "area", "column"]);

function ModalTooltip({
  content,
  children,
}: {
  content: string;
  children: React.ReactNode;
}) {
  return (
    <TooltipPrimitive.Provider delayDuration={200}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side="top"
            sideOffset={6}
            style={{
              zIndex: 20000,
              maxWidth: 320,
              padding: "6px 10px",
              backgroundColor: "var(--gray-12)",
              color: "var(--gray-1)",
              borderRadius: "var(--radius-2)",
              fontSize: 12,
              lineHeight: 1.4,
              boxShadow: "var(--shadow-4)",
            }}
          >
            {content}
            <TooltipPrimitive.Arrow style={{ fill: "var(--gray-12)" }} />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

function Field({
  label,
  width,
  children,
}: {
  label: string;
  width: number;
  children: React.ReactNode;
}) {
  return (
    <Flex direction="column" gap="1" style={{ width: `${width}px` }}>
      <Text as="div" size="2" weight="medium" color="gray">
        {label}
      </Text>
      {children}
    </Flex>
  );
}

export default function ChartCreator({
  draft,
  entityDefinition: entityDefinitionProp,
  entityData,
  platformMetric,
  onDraftChange,
}: ChartCreatorProps) {
  const dataSource = draft.dataSource;
  const chartType = draft.chartType;
  const needsLazyEntity =
    dataSource === "entity" && !entityDefinitionProp && !!draft.entityName;
  const lazyEntityQuery = useQuery({
    queryKey: ["entity", draft.entityName, { includeSystem: true }],
    queryFn: () => entityService.getByName(draft.entityName!, true),
    enabled: needsLazyEntity,
    staleTime: 5 * 60 * 1000,
  });
  const entityDefinition = entityDefinitionProp ?? lazyEntityQuery.data ?? null;
  const isStat = chartType === "stat";
  const isGauge = chartType === "gauge";
  const isStatOrGauge = isStat || isGauge;
  const isPie = chartType === "pie";
  const isFunnel = chartType === "funnel";
  const isTimeSeries = TIME_SERIES_TYPES.has(chartType);

  const patch = (partial: Partial<ChartConfiguration>) =>
    onDraftChange({ ...draft, ...partial });

  const morphedFromPlatform =
    dataSource === "entity" &&
    draft.entityName === "anythink_users" &&
    !!draft.platformMetric &&
    !!platformMetric;

  const availableChartTypes = useMemo(() => {
    if ((dataSource === "platform" || morphedFromPlatform) && platformMetric) {
      return CHART_TYPES.filter(
        (t) =>
          platformMetric.supportedChartTypes.includes(t.value) &&
          (t.value !== "pie" || platformMetric.stackByOptions.length > 0),
      );
    }
    if (dataSource === "quota") {
      // Quota always supports stat/gauge; if it has a platform equivalent (via ChartBuilderModal lookup), also expose that metric's chart types.
      return CHART_TYPES.filter((t) => {
        if (t.value === "stat" || t.value === "gauge") return true;
        if (!platformMetric) return false;
        return (
          platformMetric.supportedChartTypes.includes(t.value) &&
          (t.value !== "pie" || platformMetric.stackByOptions.length > 0)
        );
      });
    }
    return CHART_TYPES;
  }, [dataSource, platformMetric, morphedFromPlatform]);

  const debouncedDraft = useDebounce(draft, 300);

  // Entity fields (only relevant for entity source)
  const availableFields = useMemo(
    () =>
      entityDefinition?.fields.map((f) => ({
        value: f.name,
        label: f.name,
        databaseType: f.database_type,
      })) ?? [],
    [entityDefinition?.fields],
  );
  const xAxisFields = availableFields.filter((f) =>
    isXAxisField(f.databaseType),
  );
  const numericFields = availableFields.filter((f) =>
    isNumericField(f.databaseType),
  );
  const yFieldOptions = numericFields.map((f) => ({
    value: f.value,
    label: `${f.label} (${f.databaseType})`,
  }));

  const yAgg: YAgg = (draft.yAgg as YAgg) ?? "count";
  const needsYField = yAgg !== "count";
  const xFieldMeta = availableFields.find((f) => f.value === draft.xField);
  const xIsTemporal = xFieldMeta
    ? isTemporalField(xFieldMeta.databaseType)
    : false;
  const timeframe = draft.timeframePreset ?? "all";

  const entityReady = !!draft.xField && (!needsYField || !!draft.yField);
  const funnelReady = isFunnel && (draft.funnelStages?.length ?? 0) > 0;
  const platformReady = !!draft.platformMetric;
  const quotaReady = !!draft.quotaMetric;
  const isReady =
    dataSource === "entity"
      ? isStatOrGauge || funnelReady || entityReady
      : dataSource === "platform"
        ? platformReady
        : quotaReady;

  const incompleteReason =
    dataSource === "entity" &&
    !isStatOrGauge &&
    draft.xField &&
    needsYField &&
    !draft.yField
      ? `Pick a numeric Y field to see the ${yAgg} chart.`
      : null;

  const previewQuery = useQuery({
    queryKey: ["chart-preview", { ...debouncedDraft, name: undefined }],
    queryFn: () =>
      chartConfigurationService.previewChartConfig({
        name: debouncedDraft.name || "Preview",
        dataSource: debouncedDraft.dataSource,
        chartType: debouncedDraft.chartType,
        entityName: debouncedDraft.entityName ?? null,
        xField: debouncedDraft.xField ?? null,
        yField: debouncedDraft.yField ?? null,
        pieMode: debouncedDraft.pieMode ?? null,
        stackBy: debouncedDraft.stackBy ?? null,
        yAgg: debouncedDraft.yAgg ?? null,
        xInterval: debouncedDraft.xInterval ?? null,
        timeframePreset: debouncedDraft.timeframePreset ?? null,
        timeframeFrom: debouncedDraft.timeframeFrom ?? null,
        timeframeTo: debouncedDraft.timeframeTo ?? null,
        timeframeOffsetDays: debouncedDraft.timeframeOffsetDays ?? null,
        compareEnabled: debouncedDraft.compareEnabled ?? null,
        comparePreviousDays: debouncedDraft.comparePreviousDays ?? null,
        compareShowOverlay: debouncedDraft.compareShowOverlay ?? null,
        limit: debouncedDraft.limit ?? null,
        sortDirection: debouncedDraft.sortDirection ?? null,
        cumulative: debouncedDraft.cumulative ?? null,
        target: debouncedDraft.target ?? null,
        targetMode: debouncedDraft.targetMode ?? null,
        filters: debouncedDraft.filters ?? null,
        funnelStages: debouncedDraft.funnelStages ?? null,
        funnelStyle: debouncedDraft.funnelStyle ?? null,
        funnelMode: debouncedDraft.funnelMode ?? null,
        cohortField: debouncedDraft.cohortField ?? null,
        platformMetric: debouncedDraft.platformMetric ?? null,
        platformPeriod: debouncedDraft.platformPeriod ?? null,
        quotaMetric: debouncedDraft.quotaMetric ?? null,
      }),
    enabled: isReady,
  });

  type PreviewShape = {
    items?: Record<string, unknown>[];
    total?: number;
    value?: number;
    target?: number | null;
    unit?: string;
    label?: string;
    error?: string;
    chart?: {
      x_field?: string | null;
      y_field?: string | null;
      series?: string[];
    };
  };
  const rawPreview = previewQuery.data as
    | (PreviewShape & {
        primary?: PreviewShape;
        compare?: { items?: Record<string, unknown>[] } | null;
        delta_pct?: number | null;
        compare_period_days?: number | null;
      })
    | undefined;
  const previewData: PreviewShape | undefined =
    rawPreview?.primary ?? rawPreview;
  const compareItems = rawPreview?.compare?.items ?? null;
  const deltaPct = rawPreview?.delta_pct ?? null;
  const comparePeriodDays = rawPreview?.compare_period_days ?? null;
  const previewError =
    previewData?.error ??
    (previewQuery.error instanceof Error
      ? previewQuery.error.message
      : previewQuery.error
        ? "Chart preview failed. Check the network tab for details."
        : null);

  const aggregatedData: PaginatedResult<Record<string, unknown>> | null =
    useMemo(() => {
      if (!isReady || !previewData) return null;
      return {
        items: previewData.items ?? [],
        total_items: previewData.total ?? 0,
        page: 1,
        page_size: previewData.items?.length ?? 0,
        total_pages: 1,
        has_next_page: false,
        has_previous_page: false,
      } as PaginatedResult<Record<string, unknown>>;
    }, [isReady, previewData]);

  const previewChart: ChartConfiguration = useMemo(
    () => ({
      ...debouncedDraft,
      name: debouncedDraft.name || "Preview",
      xField: previewData?.chart?.x_field ?? debouncedDraft.xField ?? null,
      yField: previewData?.chart?.y_field ?? debouncedDraft.yField ?? null,
      series: previewData?.chart?.series,
    }),
    [
      debouncedDraft,
      previewData?.chart?.x_field,
      previewData?.chart?.y_field,
      previewData?.chart?.series,
    ],
  );

  const renderData = isReady ? aggregatedData : (entityData ?? null);

  const showInterval = !isPie && !isStatOrGauge && !isFunnel;
  const showCumulative = isTimeSeries;
  const showTarget = isGauge;

  // Source-specific pieces — entity picks fields, platform picks metric-catalogued dimensions
  const showEntityAxisRow =
    dataSource === "entity" && !isStatOrGauge && !isFunnel;
  const showBreakdown =
    (dataSource === "platform" || dataSource === "quota") &&
    platformMetric &&
    platformMetric.stackByOptions.length > 0 &&
    (isTimeSeries || isPie);
  const useNaturalQuota =
    dataSource === "quota" && isGauge && draft.target == null;
  const showQuotaTargetToggle = dataSource === "quota" && isGauge;

  const previewLabel =
    dataSource === "entity"
      ? (entityDefinition?.name ?? "")
      : draft.name || "Preview";

  return (
    <>
      <Card style={{ contain: "none" }}>
        <Box p="4">
          <Flex direction="column" gap="4">
            <Text size="3" weight="medium" as="div">
              Chart Configuration
            </Text>

            <Flex direction="column" gap="1">
              <Text as="div" size="2" weight="medium" color="gray">
                Chart Type
              </Text>
              <Flex gap="2" wrap="wrap">
                {availableChartTypes.map((option) => (
                  <Button
                    key={option.value}
                    variant={chartType === option.value ? "solid" : "outline"}
                    size="2"
                    onClick={() => {
                      const next: Partial<ChartConfiguration> = {
                        chartType: option.value,
                      };
                      const wasMorphed =
                        dataSource === "entity" &&
                        draft.entityName === "anythink_users" &&
                        !!draft.platformMetric;
                      if (option.value === "funnel") {
                        const isPlatformMetric =
                          dataSource === "platform" && !!draft.platformMetric;
                        if (isPlatformMetric) {
                          const seededStage = platformMetric
                            ? seedStageFromPlatformMetric(
                                draft.platformMetric!,
                                platformMetric.label,
                              )
                            : null;
                          next.dataSource = "entity";
                          next.entityName = "anythink_users";
                          next.cohortField = "id";
                          next.funnelMode = "cohort";
                          const baseline: FunnelStage = {
                            label: "All users (baseline)",
                            filters: [],
                          };
                          next.funnelStages = seededStage
                            ? [baseline, seededStage]
                            : [baseline];
                        } else if (
                          draft.funnelStages == null ||
                          draft.funnelStages.length === 0
                        ) {
                          next.funnelStages = [
                            {
                              label: `All ${entityDefinition?.name ?? "records"} (baseline)`,
                              filters: [],
                            },
                          ];
                        }
                      } else if (wasMorphed) {
                        next.dataSource = "platform";
                        next.entityName = null;
                        next.cohortField = null;
                        next.funnelMode = null;
                        next.funnelStages = null;
                        next.funnelStyle = null;
                      }
                      if (option.value === "pie" && draft.stackBy) {
                        next.stackBy = null;
                      }
                      patch(next);
                    }}
                  >
                    {option.icon}
                    {option.label}
                  </Button>
                ))}
              </Flex>
            </Flex>

            {showEntityAxisRow && (
              <Flex gap="4" wrap="wrap" align="end">
                <Field label={isPie ? "Group By" : "X-Axis Field"} width={220}>
                  {isPie && entityDefinition ? (
                    <RelationshipFieldPicker
                      entityName={entityDefinition.name}
                      value={draft.xField ?? null}
                      onChange={(v) => patch({ xField: v })}
                      placeholder="Select grouping field"
                      width={220}
                    />
                  ) : (
                    <SearchableSelect
                      value={draft.xField ?? ""}
                      onChange={(v) => patch({ xField: v || null })}
                      options={xAxisFields.map((f) => ({
                        value: f.value,
                        label: `${f.label} (${f.databaseType})`,
                      }))}
                      placeholder="Select X-axis field"
                      searchPlaceholder="Search fields..."
                    />
                  )}
                </Field>

                {!isPie && xIsTemporal && (
                  <Field label="Interval" width={140}>
                    <SearchableSelect
                      value={draft.xInterval ?? "day"}
                      onChange={(v) => patch({ xInterval: v as XInterval })}
                      options={INTERVAL_OPTIONS}
                    />
                  </Field>
                )}

                <Field label="Aggregation" width={140}>
                  <SearchableSelect
                    value={yAgg}
                    onChange={(v) =>
                      patch({
                        yAgg: v as YAgg,
                        pieMode: v === "sum" ? "sum" : "count",
                      })
                    }
                    options={AGG_OPTIONS}
                  />
                </Field>

                {needsYField && (
                  <Field label="Y Field" width={180}>
                    <SearchableSelect
                      value={draft.yField ?? ""}
                      onChange={(v) => patch({ yField: v || null })}
                      options={yFieldOptions}
                      placeholder="Select numeric field"
                      searchPlaceholder="Search fields..."
                    />
                  </Field>
                )}

                {!isPie && entityDefinition && (
                  <Field label="Stack By" width={220}>
                    <RelationshipFieldPicker
                      entityName={entityDefinition.name}
                      value={draft.stackBy ?? null}
                      onChange={(v) => patch({ stackBy: v })}
                      placeholder="None"
                      width={220}
                    />
                  </Field>
                )}
              </Flex>
            )}

            {showBreakdown && platformMetric && (
              <Flex gap="4" wrap="wrap" align="end">
                <Field label={isPie ? "Group By" : "Break down by"} width={200}>
                  <SearchableSelect
                    value={draft.stackBy ?? ""}
                    onChange={(v) => patch({ stackBy: v || null })}
                    options={[
                      ...(isPie
                        ? []
                        : [{ value: "", label: "None (single series)" }]),
                      ...platformMetric.stackByOptions.map((o) => ({
                        value: o.id,
                        label: o.label,
                      })),
                    ]}
                  />
                </Field>
              </Flex>
            )}

            <Flex gap="4" wrap="wrap" align="end">
              <Field label="Timeframe" width={180}>
                <SearchableSelect
                  value={timeframe}
                  onChange={(v) => {
                    const next: Partial<ChartConfiguration> = {
                      timeframePreset: v as TimeframePreset,
                    };
                    if (v !== "custom") {
                      next.timeframeFrom = null;
                      next.timeframeTo = null;
                    }
                    patch(next);
                  }}
                  options={TIMEFRAME_OPTIONS}
                />
              </Field>

              {timeframe === "custom" && (
                <>
                  <Field label="From" width={160}>
                    <TextField.Root
                      type="date"
                      value={draft.timeframeFrom?.slice(0, 10) ?? ""}
                      onChange={(e) =>
                        patch({
                          timeframeFrom: e.target.value
                            ? `${e.target.value}T00:00:00Z`
                            : null,
                        })
                      }
                    />
                  </Field>
                  <Field label="To" width={160}>
                    <TextField.Root
                      type="date"
                      value={draft.timeframeTo?.slice(0, 10) ?? ""}
                      onChange={(e) =>
                        patch({
                          timeframeTo: e.target.value
                            ? `${e.target.value}T23:59:59Z`
                            : null,
                        })
                      }
                    />
                  </Field>
                </>
              )}

              {timeframe !== "all" && (
                <Field label="Offset (days)" width={140}>
                  <TextField.Root
                    type="number"
                    min={0}
                    placeholder="0"
                    value={draft.timeframeOffsetDays ?? ""}
                    onChange={(e) => {
                      const v = e.target.value.trim();
                      const n = v === "" ? null : Number(v);
                      patch({
                        timeframeOffsetDays:
                          n === null || Number.isNaN(n) || n < 0 ? null : n,
                      });
                    }}
                  />
                </Field>
              )}

              {showInterval && dataSource !== "entity" && (
                <Field label="Interval" width={140}>
                  <SearchableSelect
                    value={draft.xInterval ?? "day"}
                    onChange={(v) => patch({ xInterval: v as XInterval })}
                    options={INTERVAL_OPTIONS}
                  />
                </Field>
              )}
              {isFunnel && (
                <Field label="Funnel mode" width={240}>
                  <Flex gap="2" wrap="wrap" align="center">
                    {(
                      [
                        { value: "rows", label: "Entity" },
                        { value: "cohort", label: "Cohort" },
                      ] as const
                    ).map((m) => (
                      <Button
                        key={m.value}
                        size="2"
                        type="button"
                        variant={
                          (draft.funnelMode ?? "rows") === m.value
                            ? "solid"
                            : "outline"
                        }
                        onClick={() =>
                          patch({ funnelMode: m.value as FunnelMode })
                        }
                      >
                        {m.label}
                      </Button>
                    ))}
                    <ModalTooltip content="Entity mode: one entity, cumulative filters. Cohort mode: distinct users across multiple entities, per-stage entity picking, time windows, and minimum-event thresholds.">
                      <IconButton
                        size="1"
                        variant="ghost"
                        color="gray"
                        type="button"
                        aria-label="Funnel mode help"
                      >
                        <Info size={14} />
                      </IconButton>
                    </ModalTooltip>
                  </Flex>
                </Field>
              )}
              {isFunnel &&
                (draft.funnelMode ?? "rows") === "cohort" &&
                entityDefinition && (
                  <Field label="Cohort field" width={220}>
                    <InlineCohortFieldPicker
                      entityDefinition={entityDefinition}
                      value={draft.cohortField ?? null}
                      onChange={(cohortField) => patch({ cohortField })}
                    />
                  </Field>
                )}

              {showCumulative && (
                <Field label="Cumulative" width={130}>
                  <Flex align="center" gap="2" style={{ height: 32 }}>
                    <Switch
                      checked={!!draft.cumulative}
                      onCheckedChange={(v) => patch({ cumulative: v })}
                    />
                    <Text size="2" color="gray">
                      Running total
                    </Text>
                  </Flex>
                </Field>
              )}

              {showQuotaTargetToggle && (
                <Field label="Target source" width={180}>
                  <Flex align="center" gap="2" style={{ height: 32 }}>
                    <Switch
                      checked={useNaturalQuota}
                      onCheckedChange={(v) => patch({ target: v ? null : 0 })}
                    />
                    <Text size="2" color="gray">
                      Use quota
                    </Text>
                  </Flex>
                </Field>
              )}

              {showTarget && !(showQuotaTargetToggle && useNaturalQuota) && (
                <Field label="Target" width={140}>
                  <TextField.Root
                    type="number"
                    min={0}
                    placeholder="e.g. 1000"
                    value={draft.target ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      patch({ target: v === "" ? null : Number(v) });
                    }}
                  />
                </Field>
              )}
              {showTarget && (
                <Field label="Target is" width={120}>
                  <SearchableSelect
                    value={draft.targetMode ?? "warning"}
                    onChange={(v) =>
                      patch({ targetMode: v as "goal" | "warning" })
                    }
                    options={[
                      { value: "warning", label: "A limit" },
                      { value: "goal", label: "A goal" },
                    ]}
                  />
                </Field>
              )}
            </Flex>

            {!isFunnel && (
              <Flex gap="4" wrap="wrap" align="end" mt="3">
                <Field label="Compare" width={140}>
                  <Flex align="center" gap="2" style={{ height: "32px" }}>
                    <Switch
                      checked={draft.compareEnabled ?? false}
                      onCheckedChange={(v) => patch({ compareEnabled: v })}
                      size="2"
                    />
                    <Text size="1" color="gray">
                      {draft.compareEnabled ? "On" : "Off"}
                    </Text>
                  </Flex>
                </Field>

                {draft.compareEnabled && (
                  <>
                    <Field
                      label="Compare against previous (days)"
                      width={240}
                    >
                      <TextField.Root
                        type="number"
                        min={1}
                        step={1}
                        placeholder="Same as primary duration"
                        value={draft.comparePreviousDays ?? ""}
                        onChange={(e) => {
                          const v = e.target.value.trim();
                          const n = v === "" ? null : Number(v);
                          patch({
                            comparePreviousDays:
                              n === null || Number.isNaN(n) || n < 1 ? null : Math.trunc(n),
                          });
                        }}
                      />
                    </Field>
                    <Field label="Show on chart" width={140}>
                      <Flex align="center" gap="2" style={{ height: "32px" }}>
                        <Switch
                          checked={draft.compareShowOverlay ?? false}
                          onCheckedChange={(v) => patch({ compareShowOverlay: v })}
                          size="2"
                        />
                        <Text size="1" color="gray">
                          {draft.compareShowOverlay ? "On" : "Off"}
                        </Text>
                      </Flex>
                    </Field>
                  </>
                )}
              </Flex>
            )}
            {dataSource === "entity" && entityDefinition && !isFunnel && (
              <Box mt="2">
                <ChartFilterBuilder
                  entityName={entityDefinition.name}
                  entity={entityDefinition}
                  value={draft.filters ?? null}
                  onChange={(filters) => patch({ filters })}
                />
              </Box>
            )}
            {dataSource === "platform" && platformMetric && (
              <Box mt="2">
                <PlatformFilterBuilder
                  dimensions={platformMetric.stackByOptions}
                  value={draft.filters ?? null}
                  onChange={(filters) => patch({ filters })}
                />
              </Box>
            )}
            {isFunnel && entityDefinition && (
              <Box mt="3">
                <Flex direction="column" gap="2" mb="3">
                  <Text as="div" size="2" weight="medium" color="gray">
                    Funnel style
                  </Text>
                  <Flex gap="2" wrap="wrap">
                    {(
                      [
                        { value: "vertical", label: "Vertical" },
                        { value: "horizontal", label: "Horizontal" },
                        { value: "sankey", label: "Sankey" },
                      ] as const
                    ).map((s) => (
                      <Button
                        key={s.value}
                        size="2"
                        type="button"
                        variant={
                          (draft.funnelStyle ?? "vertical") === s.value
                            ? "solid"
                            : "outline"
                        }
                        onClick={() => patch({ funnelStyle: s.value })}
                      >
                        {s.label}
                      </Button>
                    ))}
                  </Flex>
                </Flex>
                <FunnelStagesEditor
                  entityName={entityDefinition.name}
                  entity={entityDefinition}
                  value={draft.funnelStages ?? null}
                  onChange={(funnelStages) => patch({ funnelStages })}
                  cohortMode={(draft.funnelMode ?? "rows") === "cohort"}
                  cohortField={draft.cohortField ?? null}
                />
              </Box>
            )}
          </Flex>
        </Box>
      </Card>

      <Card>
        <Box p="4">
          <Flex justify="between" align="center" mb="4">
            <Text size="4" weight="medium">
              {draft.name || previewLabel}
            </Text>
          </Flex>

          {incompleteReason ? (
            <Flex align="center" justify="center" style={{ height: "400px" }}>
              <Text size="2" color="gray">
                {incompleteReason}
              </Text>
            </Flex>
          ) : previewError ? (
            <Flex align="center" justify="center" style={{ height: "400px" }}>
              <Text size="2" color="red">
                {previewError}
              </Text>
            </Flex>
          ) : isReady && previewQuery.isLoading ? (
            <Flex align="center" justify="center" style={{ height: "400px" }}>
              <Text size="2" color="gray">
                Loading chart…
              </Text>
            </Flex>
          ) : isStat ? (
            <Flex direction="column" align="center" gap="2">
              <StatBody
                value={previewData?.value ?? 0}
                label={
                  previewData?.unit === "currency"
                    ? undefined
                    : (previewData?.label ?? "records")
                }
                unit={previewData?.unit}
              />
              {deltaPct !== null && deltaPct !== undefined && (
                <DeltaBadge value={deltaPct} periodDays={comparePeriodDays} />
              )}
            </Flex>
          ) : isGauge ? (
            <Flex direction="column" align="center" gap="2">
              <GaugeBody
                value={previewData?.value ?? 0}
                target={draft.target ?? previewData?.target ?? null}
                unit={previewData?.unit}
                targetMode={draft.targetMode ?? "warning"}
              />
              {deltaPct !== null && deltaPct !== undefined && (
                <DeltaBadge value={deltaPct} periodDays={comparePeriodDays} />
              )}
            </Flex>
          ) : isFunnel ? (
            <FunnelBody
              stages={
                (
                  previewData as
                    | { stages?: { label: string; count: number }[] }
                    | undefined
                )?.stages ?? []
              }
              style={draft.funnelStyle ?? "vertical"}
            />
          ) : (
            <Flex direction="column" gap="2">
              {deltaPct !== null && deltaPct !== undefined && (
                <DeltaBadge value={deltaPct} periodDays={comparePeriodDays} />
              )}
              <ChartRenderer
                chart={previewChart}
                entityData={renderData}
                compareItems={compareItems}
                height="400px"
              />
            </Flex>
          )}
        </Box>
      </Card>
    </>
  );
}

function StatBody({
  value,
  label,
  unit,
}: {
  value: number;
  label?: string;
  unit?: string;
}) {
  const formatted =
    unit === "currency"
      ? new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: "GBP",
        }).format(value)
      : value.toLocaleString();
  return (
    <Flex
      align="center"
      justify="center"
      direction="column"
      gap="2"
      style={{ height: "400px" }}
    >
      <Heading size="9" weight="bold">
        {formatted}
      </Heading>
      {label && (
        <Text size="2" color="gray">
          {label}
        </Text>
      )}
    </Flex>
  );
}

function GaugeBody({
  value,
  target,
  unit,
  targetMode = "warning",
}: {
  value: number;
  target?: number | null;
  unit?: string;
  targetMode?: "goal" | "warning";
}) {
  const hasTarget = target != null && target > 0;
  const pct = hasTarget ? Math.min(100, (value / target!) * 100) : 0;
  const color: "green" | "amber" | "red" =
    targetMode === "goal"
      ? pct >= 90
        ? "green"
        : pct >= 60
          ? "amber"
          : "red"
      : pct >= 90
        ? "red"
        : pct >= 70
          ? "amber"
          : "green";
  return (
    <Flex
      align="center"
      justify="center"
      direction="column"
      gap="3"
      style={{ height: "400px" }}
    >
      <Flex align="baseline" gap="2">
        <Heading size="8" weight="bold">
          {value.toLocaleString()}
        </Heading>
        {hasTarget && (
          <Text size="3" color="gray">
            of {target!.toLocaleString()}
            {unit ? ` ${unit}` : ""}
          </Text>
        )}
      </Flex>
      {hasTarget ? (
        <>
          <Box style={{ width: "60%", minWidth: 200 }}>
            <Progress value={pct} color={color} size="3" />
          </Box>
          <Text size="2" color="gray">
            {pct.toFixed(1)}%
          </Text>
        </>
      ) : (
        <Text size="2" color="gray">
          Set a target to see progress
        </Text>
      )}
    </Flex>
  );
}

function safeUuid(): string {
  const c = typeof crypto !== "undefined" ? (crypto as Crypto) : null;
  return c?.randomUUID
    ? c.randomUUID()
    : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

type PaymentFilter = {
  status?: PaymentStatus;
  provider?: PaymentProvider;
  type?: string;
  currency?: string;
};
type SubscriptionFilter = {
  status?: SubscriptionStatus;
  provider?: PaymentProvider;
  billingInterval?: BillingInterval;
  type?: string;
  currency?: string;
};
type SubscriptionEventFilter = {
  eventType?: SubscriptionEventType;
  source?: SubscriptionEventSource;
  newStatus?: SubscriptionStatus;
  priorStatus?: SubscriptionStatus;
};

function seedStageFromPlatformMetric(
  metricId: string,
  metricLabel: string,
): FunnelStage | null {
  const payApi = (
    source: NonNullable<FunnelStage["source"]>,
    sourceFilter: Record<string, string> = {},
  ): FunnelStage => ({
    label: metricLabel,
    source,
    sourceFilter,
    filters: [],
  });
  const entity = (
    entityName: string,
    filters: FunnelStage["filters"] = [],
  ): FunnelStage => ({
    label: metricLabel,
    entityName,
    filters,
  });
  switch (metricId) {
    case "payments":
      return payApi("payments");
    case "payments_revenue":
    case "payments_net_revenue":
    case "paying_customers":
      return payApi("payments", {
        status: "Completed",
      } satisfies PaymentFilter);
    case "payments_avg_value":
      return payApi("payments", {
        status: "Completed",
      } satisfies PaymentFilter);
    case "subscriptions":
      return payApi("subscriptions");
    case "subscriptions_mrr":
      return payApi("subscriptions", {
        status: "active",
        billingInterval: "month",
      } satisfies SubscriptionFilter);
    case "subscriptions_arr":
      return payApi("subscriptions", {
        status: "active",
        billingInterval: "year",
      } satisfies SubscriptionFilter);
    case "active_subscribers":
      return payApi("subscriptions", {
        status: "active",
      } satisfies SubscriptionFilter);
    case "subscriptions_churned":
      return payApi("subscription_events", {
        eventType: "cancelled",
      } satisfies SubscriptionEventFilter);
    case "subscriptions_renewed":
      return payApi("subscription_events", {
        eventType: "renewed",
      } satisfies SubscriptionEventFilter);
    case "subscriptions_trial_started":
      return payApi("subscription_events", {
        eventType: "trial_started",
      } satisfies SubscriptionEventFilter);
    case "subscriptions_refunded":
      return payApi("subscription_events", {
        eventType: "refunded",
      } satisfies SubscriptionEventFilter);
    case "subscription_events":
      return payApi("subscription_events");
    case "code_redemptions":
      return payApi("offer_redemptions");
    case "workflow_runs":
      return entity("anythink_workflow_jobs");
    case "files":
      return entity("anythink_files");
    case "active_users":
    case "users":
    default:
      return null;
  }
}

const STAGE_SOURCE_OPTIONS: {
  value: NonNullable<FunnelStage["source"]>;
  label: string;
}[] = [
  { value: "entity", label: "Entity in this project" },
  { value: "payments", label: "AnythinkPay — Payments" },
  {
    value: "subscriptions",
    label: "AnythinkPay — Subscriptions (current state)",
  },
  { value: "subscription_events", label: "AnythinkPay — Subscription events" },
  { value: "offer_redemptions", label: "AnythinkPay — Code redemptions" },
];

const STAGE_SOURCE_FIELDS: Record<
  Exclude<NonNullable<FunnelStage["source"]>, "entity">,
  { key: string; label: string; enumKey?: string; placeholder?: string }[]
> = {
  payments: [
    { key: "status", label: "Status", enumKey: "payment_status" },
    { key: "provider", label: "Provider", enumKey: "payment_provider" },
    {
      key: "type",
      label: "Type",
      placeholder: "web / mobile / charge / recurring",
    },
    { key: "currency", label: "Currency", placeholder: "gbp / usd / eur" },
  ],
  subscriptions: [
    { key: "status", label: "Status", enumKey: "subscription_status" },
    { key: "provider", label: "Provider", enumKey: "payment_provider" },
    {
      key: "billingInterval",
      label: "Billing interval",
      enumKey: "billing_interval",
    },
    { key: "type", label: "Type" },
    { key: "currency", label: "Currency" },
  ],
  subscription_events: [
    {
      key: "eventType",
      label: "Event type",
      enumKey: "subscription_event_type",
    },
    {
      key: "source",
      label: "Triggered by",
      enumKey: "subscription_event_source",
    },
    {
      key: "newStatus",
      label: "Changed to status",
      enumKey: "subscription_status",
    },
    {
      key: "priorStatus",
      label: "Changed from status",
      enumKey: "subscription_status",
    },
  ],
  offer_redemptions: [
    { key: "offerId", label: "Offer ID (UUID)" },
    {
      key: "redeemerRewardStatus",
      label: "Reward status",
      enumKey: "reward_status",
    },
  ],
};

const OP_LABELS: Record<string, string> = {
  eq: "=",
  ne: "≠",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  contains: "contains",
  in: "in",
  exists: "exists",
  not_exists: "does not exist",
};

function summarizeFilters(filters: FunnelStage["filters"]): string {
  if (!filters || filters.length === 0) return "";
  const complete = filters.filter((f) => {
    if (!f.field) return false;
    if (f.op === "exists" || f.op === "not_exists") return true;
    return f.value != null && f.value !== "";
  });
  if (complete.length === 0) return "";
  const first = complete[0];
  const opLabel = OP_LABELS[first.op] ?? first.op;
  const head =
    first.op === "exists" || first.op === "not_exists"
      ? `${first.field} ${opLabel}`
      : `${first.field} ${opLabel} ${first.value}`;
  return complete.length === 1 ? head : `${head} + ${complete.length - 1} more`;
}

function computeStageAutoLabel(
  stage: FunnelStage,
  cohortMode: boolean,
  baseEntityName: string,
  index: number,
): string {
  const summary = summarizeFilters(stage.filters);
  const entities = cohortMode
    ? stage.entityNames?.length
      ? stage.entityNames
      : stage.entityName
        ? [stage.entityName]
        : [baseEntityName]
    : [];
  const entityLabel =
    entities.length === 0
      ? ""
      : entities.length === 1
        ? entities[0]
        : entities.length === 2
          ? `${entities[0]} or ${entities[1]}`
          : `${entities[0]} + ${entities.length - 1} more`;

  if (cohortMode) {
    if (summary && entityLabel) return `${entityLabel} · ${summary}`;
    if (summary) return summary;
    if (entityLabel) return `Any ${entityLabel}`;
    return `Stage ${index + 1}`;
  }
  return summary || `Stage ${index + 1}`;
}

function stageEntities(stage: FunnelStage, _baseEntity: string): string[] {
  if (stage.entityNames && stage.entityNames.length > 0)
    return stage.entityNames;
  if (stage.entityName) return [stage.entityName];
  return [];
}

function isLabelOnAutoTrack(
  currentLabel: string,
  previousAutoLabel: string,
  index: number,
): boolean {
  if (!currentLabel) return true;
  if (currentLabel === previousAutoLabel) return true;
  if (currentLabel === `Stage ${index + 1}`) return true;
  return false;
}

function InlineCohortFieldPicker({
  entityDefinition,
  value,
  onChange,
}: {
  entityDefinition: IEntity;
  value: string | null;
  onChange: (v: string) => void;
}) {
  const candidates = React.useMemo(() => {
    const preferred = ["user_id", "user_profile_id"];
    const names = new Set(entityDefinition.fields.map((f) => f.name));
    const options: { value: string; label: string }[] = [];
    for (const p of preferred)
      if (names.has(p)) options.push({ value: p, label: p });
    for (const f of entityDefinition.fields) {
      if (options.some((o) => o.value === f.name)) continue;
      if (f.database_type === "user" || f.name.endsWith("_id")) {
        options.push({ value: f.name, label: f.name });
      }
    }
    for (const f of entityDefinition.fields) {
      if (options.some((o) => o.value === f.name)) continue;
      options.push({ value: f.name, label: f.name });
    }
    return options;
  }, [entityDefinition]);

  const current = value ?? (candidates.length > 0 ? candidates[0].value : "");
  React.useEffect(() => {
    if (!value && candidates.length > 0) onChange(candidates[0].value);
  }, [value, candidates, onChange]);

  if (candidates.length === 0) {
    return (
      <Text size="1" color="red">
        No fields available
      </Text>
    );
  }
  return (
    <SearchableSelect
      value={current}
      onChange={(v) => onChange(v)}
      options={candidates}
      placeholder="Cohort field"
      width={220}
    />
  );
}

function StageEntitiesPicker({
  values,
  fallback,
  cohortField,
  onChange,
}: {
  values: string[];
  fallback: string;
  cohortField: string | null;
  onChange: (v: string[]) => void;
}) {
  const { data: entities } = useQuery({
    queryKey: ["entities-all"],
    queryFn: () => entityService.getAll(),
    staleTime: 5 * 60 * 1000,
  });

  const options = React.useMemo(() => {
    if (!entities) return [];
    const filtered = cohortField
      ? entities.filter((e) => e.fields.some((f) => f.name === cohortField))
      : entities;
    return filtered
      .filter((e) => !values.includes(e.name))
      .map((e) => ({ value: e.name, label: e.name }));
  }, [entities, cohortField, values]);

  const remove = (name: string) => onChange(values.filter((v) => v !== name));
  const add = (name: string) => {
    if (!name || values.includes(name)) return;
    onChange([...values, name]);
  };

  return (
    <Flex direction="column" gap="2">
      {values.length > 0 && (
        <Text size="1" color="gray">
          {values.length === 1
            ? "Stage matches users with data in this entity:"
            : "Stage matches users with data in ANY of these entities:"}
        </Text>
      )}
      <Flex gap="2" wrap="wrap">
        {values.length === 0 && (
          <Text size="1" color="gray" style={{ padding: "4px 0" }}>
            {fallback}
          </Text>
        )}
        {values.map((name) => (
          <Box
            key={name}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 8px 4px 10px",
              backgroundColor: "var(--accent-3)",
              border: "1px solid var(--accent-6)",
              borderRadius: "var(--radius-3)",
              fontSize: 12,
              lineHeight: 1.2,
            }}
          >
            <span>{name}</span>
            <button
              type="button"
              onClick={() => remove(name)}
              aria-label={`Remove ${name}`}
              style={{
                all: "unset",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 16,
                height: 16,
                borderRadius: "50%",
                color: "var(--gray-11)",
              }}
            >
              <X size={11} />
            </button>
          </Box>
        ))}
      </Flex>
      <Box style={{ maxWidth: 260 }}>
        <SearchableSelect
          value=""
          onChange={(v) => add(v)}
          options={options}
          placeholder={
            values.length === 0 ? "Pick an entity" : "+ Add another entity"
          }
          width={260}
        />
      </Box>
    </Flex>
  );
}

function FunnelStagesEditor({
  entityName,
  entity,
  value,
  onChange,
  cohortMode = false,
  cohortField = null,
}: {
  entityName: string;
  entity?: IEntity | null;
  value: FunnelStage[] | null;
  onChange: (stages: FunnelStage[]) => void;
  cohortMode?: boolean;
  cohortField?: string | null;
}) {
  const stages = value ?? [];
  const [ids, setIds] = React.useState<string[]>(() =>
    stages.map(() => safeUuid()),
  );

  React.useEffect(() => {
    setIds((prev) => {
      if (prev.length === stages.length) return prev;
      if (prev.length < stages.length) {
        const extra = Array.from({ length: stages.length - prev.length }, () =>
          safeUuid(),
        );
        return [...prev, ...extra];
      }
      return prev.slice(0, stages.length);
    });
  }, [stages.length]);

  const setRow = (index: number, patch: Partial<FunnelStage>) => {
    onChange(
      stages.map((s, i) => {
        if (i !== index) return s;
        const merged = { ...s, ...patch };
        const touchesContent =
          "filters" in patch ||
          "entityName" in patch ||
          "entityNames" in patch ||
          "withinDays" in patch ||
          "minCount" in patch;
        const userTouchedLabel = "label" in patch;
        if (index > 0 && touchesContent && !userTouchedLabel) {
          const prevAuto = computeStageAutoLabel(
            s,
            cohortMode,
            entityName,
            index,
          );
          if (isLabelOnAutoTrack(s.label, prevAuto, index)) {
            const nextAuto = computeStageAutoLabel(
              merged,
              cohortMode,
              entityName,
              index,
            );
            if (nextAuto) merged.label = nextAuto;
          }
        }
        return merged;
      }),
    );
  };

  const removeRow = (index: number) => {
    setIds((prev) => prev.filter((_, i) => i !== index));
    onChange(stages.filter((_, j) => j !== index));
  };

  const addRow = () => {
    setIds((prev) => [...prev, safeUuid()]);
    onChange([...stages, { label: `Stage ${stages.length + 1}`, filters: [] }]);
  };

  const baseline = stages[0];
  const tail = stages.slice(1).map((stage, i) => ({
    id: ids[i + 1] ?? `t-${i}`,
    stage,
    absIndex: i + 1,
  }));

  const handleReorder = (reordered: typeof tail) => {
    const newTailStages = reordered.map((r) => r.stage);
    const newTailIds = reordered.map((r) => r.id);
    setIds([ids[0] ?? safeUuid(), ...newTailIds]);
    onChange(baseline ? [baseline, ...newTailStages] : newTailStages);
  };

  const renderStageBox = (
    stage: FunnelStage,
    absIndex: number,
    isBaseline: boolean,
    dragHandle?: React.HTMLAttributes<HTMLElement>,
  ) => (
    <Box
      p="3"
      style={{
        border: `1px solid var(${isBaseline ? "--accent-6" : "--gray-5"})`,
        borderRadius: "var(--radius-3)",
        backgroundColor: "var(--color-panel-solid)",
      }}
    >
      <Flex direction="column" gap="2">
        <Flex gap="2" align="center">
          {isBaseline ? (
            <Text size="1" color="gray" style={{ width: 24 }}>
              #{absIndex + 1}
            </Text>
          ) : (
            <Flex gap="1" align="center" style={{ width: 44, flexShrink: 0 }}>
              <Box
                {...(dragHandle ?? {})}
                style={{
                  cursor: "grab",
                  display: "inline-flex",
                  alignItems: "center",
                  color: "var(--gray-9)",
                }}
                aria-label="Drag stage"
              >
                <GripVertical size={14} />
              </Box>
              <Text size="1" color="gray">
                #{absIndex + 1}
              </Text>
            </Flex>
          )}
          <TextField.Root
            style={{ flex: 1 }}
            value={stage.label}
            onChange={(e) => setRow(absIndex, { label: e.target.value })}
            placeholder={isBaseline ? "Baseline label" : "Stage label"}
          />
          {isBaseline ? (
            <Text size="1" color="gray" style={{ paddingRight: 4 }}>
              baseline · created_at
            </Text>
          ) : (
            <Button
              size="1"
              variant="soft"
              color="red"
              type="button"
              onClick={() => removeRow(absIndex)}
            >
              Remove
            </Button>
          )}
        </Flex>
        {isBaseline && (
          <Text size="1" color="gray">
            {cohortMode
              ? `Baseline cohort — counts distinct ${cohortField ?? "cohort"} values in ${entityName} within the timeframe.`
              : `Counts every ${entityName} record in the chart's timeframe (anchored on created_at). Add filters to narrow the starting cohort.`}
          </Text>
        )}
        {!isBaseline && cohortMode && (
          <Flex direction="column" gap="2">
            <Flex direction="column" gap="1" style={{ maxWidth: 420 }}>
              <Text as="div" size="1" color="gray">
                Stage source
              </Text>
              <SearchableSelect
                value={stage.source ?? "entity"}
                onChange={(v) => {
                  const next = (v || "entity") as NonNullable<
                    FunnelStage["source"]
                  >;
                  const isEntity = next === "entity";
                  setRow(absIndex, {
                    source: isEntity ? null : next,
                    sourceFilter: isEntity ? null : {},
                    entityName: isEntity ? stage.entityName : null,
                    entityNames: isEntity ? stage.entityNames : null,
                    filters: isEntity ? stage.filters : [],
                  });
                }}
                options={STAGE_SOURCE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
            </Flex>
            {(!stage.source || stage.source === "entity") && (
              <Flex gap="3" wrap="wrap" align="end">
                <Flex
                  direction="column"
                  gap="1"
                  style={{ flex: "1 1 260px", minWidth: 260 }}
                >
                  <Text as="div" size="1" color="gray">
                    Stage entity
                  </Text>
                  <StageEntitiesPicker
                    values={
                      stage.entityNames && stage.entityNames.length > 0
                        ? stage.entityNames
                        : stage.entityName
                          ? [stage.entityName]
                          : []
                    }
                    fallback={entityName}
                    cohortField={cohortField}
                    onChange={(vs) =>
                      setRow(absIndex, {
                        entityNames: vs.length > 1 ? vs : null,
                        entityName: vs.length > 0 ? vs[0] : null,
                      })
                    }
                  />
                </Flex>
                <Flex direction="column" gap="1" style={{ width: 190 }}>
                  <Text as="div" size="1" color="gray">
                    Within N days of baseline
                    <Text
                      size="1"
                      color="gray"
                      style={{ display: "block", opacity: 0.75, fontSize: 10 }}
                    >
                      Bounds this stage's row created_at
                    </Text>
                  </Text>
                  <TextField.Root
                    type="number"
                    min={1}
                    placeholder="Any time"
                    value={
                      stage.withinDays == null ? "" : String(stage.withinDays)
                    }
                    onChange={(e) =>
                      setRow(absIndex, {
                        withinDays:
                          e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                  />
                </Flex>
                <Flex direction="column" gap="1" style={{ width: 170 }}>
                  <Text as="div" size="1" color="gray">
                    Minimum events per user
                    <Text
                      size="1"
                      color="gray"
                      style={{ display: "block", opacity: 0.75, fontSize: 10 }}
                    >
                      User needs ≥ this many rows
                    </Text>
                  </Text>
                  <TextField.Root
                    type="number"
                    min={1}
                    placeholder="1"
                    value={stage.minCount == null ? "" : String(stage.minCount)}
                    onChange={(e) =>
                      setRow(absIndex, {
                        minCount:
                          e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                  />
                </Flex>
              </Flex>
            )}
            {stage.source && stage.source !== "entity" && (
              <SourceFilterForm
                stage={stage}
                onPatch={(patch) => setRow(absIndex, patch)}
              />
            )}
          </Flex>
        )}
        {(!stage.source || stage.source === "entity") &&
          (() => {
            const stageEntityList = stageEntities(stage, entityName);
            const isMultiEntity =
              cohortMode && !isBaseline && stageEntityList.length > 1;
            return (
              <Flex direction="column" gap="1">
                <ChartFilterBuilder
                  entityName={
                    cohortMode && !isBaseline
                      ? (stageEntityList[0] ?? entityName)
                      : entityName
                  }
                  entity={cohortMode && !isBaseline ? undefined : entity}
                  value={stage.filters ?? null}
                  onChange={(filters) => setRow(absIndex, { filters })}
                />
                {isMultiEntity && (
                  <Text size="1" color="gray">
                    Filters use the first entity's fields but apply to every
                    entity in this stage where the same column exists — others
                    are counted without that filter.
                  </Text>
                )}
              </Flex>
            );
          })()}
      </Flex>
    </Box>
  );

  return (
    <Flex direction="column" gap="2">
      <Flex justify="between" align="center">
        <Text as="label" size="2" weight="medium">
          Funnel stages
        </Text>
        <Button size="1" variant="soft" type="button" onClick={addRow}>
          + Add stage
        </Button>
      </Flex>
      {stages.length === 0 && (
        <Text size="1" color="gray">
          No stages yet. Add stages to build the funnel.
        </Text>
      )}
      {baseline && renderStageBox(baseline, 0, true)}
      {tail.length > 0 && (
        <SortableList
          items={tail}
          getItemId={(t) => t.id}
          onReorder={handleReorder}
          renderItem={(t) => (
            <SortableItem key={t.id} id={t.id}>
              {({ dragHandleProps }) =>
                renderStageBox(t.stage, t.absIndex, false, dragHandleProps)
              }
            </SortableItem>
          )}
          renderDragOverlay={(t) => renderStageBox(t.stage, t.absIndex, false)}
          containerStyle={{ gap: "var(--space-2)" }}
        />
      )}
    </Flex>
  );
}

function SourceFilterForm({
  stage,
  onPatch,
}: {
  stage: FunnelStage;
  onPatch: (patch: Partial<FunnelStage>) => void;
}) {
  const { data: enumsMap } = useQuery({
    queryKey: ["enums"],
    queryFn: () => enumsService.getAll(),
    staleTime: 30 * 60 * 1000,
  });
  const source = stage.source;
  if (!source || source === "entity") return null;
  const fields = STAGE_SOURCE_FIELDS[source];
  const filter = stage.sourceFilter ?? {};
  const setKey = (key: string, value: string | null) => {
    const next = { ...filter };
    if (value == null || value === "") delete next[key];
    else next[key] = value;
    onPatch({ sourceFilter: next });
  };
  return (
    <Flex direction="column" gap="2">
      <Flex gap="3" wrap="wrap" align="end">
        {fields.map((f) => {
          const options = f.enumKey ? enumsMap?.[f.enumKey] : undefined;
          return (
            <Flex
              key={f.key}
              direction="column"
              gap="1"
              style={{ minWidth: 180 }}
            >
              <Text as="div" size="1" color="gray">
                {f.label}
              </Text>
              {options ? (
                <SearchableSelect
                  value={filter[f.key] ?? ""}
                  onChange={(v) => setKey(f.key, v)}
                  options={[{ value: "", label: "Any" }, ...options]}
                  placeholder="Any"
                />
              ) : (
                <TextField.Root
                  placeholder={f.placeholder ?? "Any"}
                  value={filter[f.key] ?? ""}
                  onChange={(e) => setKey(f.key, e.target.value)}
                />
              )}
            </Flex>
          );
        })}
        <Flex direction="column" gap="1" style={{ width: 180 }}>
          <Text as="div" size="1" color="gray">
            Within N days of baseline
          </Text>
          <TextField.Root
            type="number"
            min={1}
            placeholder="Any time"
            value={stage.withinDays == null ? "" : String(stage.withinDays)}
            onChange={(e) =>
              onPatch({
                withinDays:
                  e.target.value === "" ? null : Number(e.target.value),
              })
            }
          />
        </Flex>
        {(source === "payments" || source === "subscription_events") && (
          <Flex direction="column" gap="1" style={{ width: 180 }}>
            <Text as="div" size="1" color="gray">
              Minimum events per user
            </Text>
            <TextField.Root
              type="number"
              min={1}
              placeholder="1"
              value={stage.minCount == null ? "" : String(stage.minCount)}
              onChange={(e) =>
                onPatch({
                  minCount:
                    e.target.value === "" ? null : Number(e.target.value),
                })
              }
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

function FunnelBody({
  stages,
  style,
}: {
  stages: { label: string; count: number }[];
  style: FunnelStyle;
}) {
  return <FunnelChartView stages={stages} style={style} height="400px" />;
}
